import { Router } from '@angular/router';

export type LoginNavigateTarget =
  | { kind: 'external-distributed-reports' }
  | { kind: 'reports'; tab: string }
  | { kind: 'query'; section: string }
  | { kind: 'claim'; recordSource: string; recordId: string }
  | { kind: 'home' }
  | { kind: 'unmatched'; raw: string | null; normalized: string };

/** Classic parity: email navigateto tokens are matched case-insensitively. */
export function normalizeLoginNavigateToken(
  value: string | null | undefined,
): string | null {
  if (value == null) {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed.toLowerCase();
}

export interface LoginDeepLinkParams {
  navigateTo: string | null;
  reportCode: string | null;
  recordSource: string | null;
  recordId: string | null;
}

export function resolveLoginNavigateTarget(
  params: LoginDeepLinkParams,
): LoginNavigateTarget {
  if (params.reportCode?.trim()) {
    return { kind: 'external-distributed-reports' };
  }

  const normalized = normalizeLoginNavigateToken(params.navigateTo);
  if (!normalized) {
    return { kind: 'home' };
  }

  switch (normalized) {
    case 'completedreports':
      return { kind: 'reports', tab: 'completed' };
    case 'pendingrequests':
      return { kind: 'reports', tab: 'pending-reports' };
    case 'pendingfilters':
      return { kind: 'reports', tab: 'pending-filters' };
    case 'completedqueries':
      return { kind: 'query', section: 'view-completed' };
    case 'reportcode':
      return { kind: 'external-distributed-reports' };
    case 'claim':
      if (params.recordSource?.trim() && params.recordId?.trim()) {
        return {
          kind: 'claim',
          recordSource: params.recordSource.trim(),
          recordId: params.recordId.trim(),
        };
      }
      return { kind: 'home' };
    default:
      return {
        kind: 'unmatched',
        raw: params.navigateTo,
        normalized,
      };
  }
}

export function navigateAfterLogin(
  router: Router,
  target: LoginNavigateTarget,
): void {
  switch (target.kind) {
    case 'external-distributed-reports':
      void router.navigate(['/external-distributed-reports']);
      break;
    case 'reports':
      void router.navigate(['/reports'], {
        queryParams: { tab: target.tab },
      });
      break;
    case 'query':
      void router.navigate(['/query'], {
        queryParams: { section: target.section },
      });
      break;
    case 'claim':
      void router.navigate([
        '/view/claim',
        target.recordSource,
        target.recordId,
      ]);
      break;
    case 'home':
    case 'unmatched':
      void router.navigate(['/']);
      break;
  }
}

/** Query params preserved through MFA → OTP for email deep links. */
export function loginDeepLinkQueryParams(
  params: LoginDeepLinkParams,
): Record<string, string> {
  const query: Record<string, string> = {};
  if (params.navigateTo?.trim()) {
    query['navigateto'] = params.navigateTo.trim();
  }
  if (params.reportCode?.trim()) {
    query['reportcode'] = params.reportCode.trim();
  }
  if (params.recordSource?.trim()) {
    query['recordSource'] = params.recordSource.trim();
  }
  if (params.recordId?.trim()) {
    query['recordId'] = params.recordId.trim();
  }
  return query;
}

export function readLoginDeepLinkFromQuery(
  query: Record<string, string | undefined>,
): LoginDeepLinkParams {
  return {
    navigateTo: query['navigateto'] ?? null,
    reportCode: query['reportcode'] ?? null,
    recordSource: query['recordSource'] ?? null,
    recordId: query['recordId'] ?? null,
  };
}
